import { useQuery } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { Badge } from "@/components/ui"
import { DataGrid, type ColumnDef } from "@/components/DataGrid"
import {
  fetchAnlage,
  fetchKomponenten,
  fetchObjekt,
  fetchPruefungen,
  fetchWartungen,
  type Komponente,
} from "@/lib/api"

const FOCUS_LINK_CLASS =
  "rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"

/** Grid-Spalten der Komponenten-Liste (statisch, daher auf Modulebene) —
 * header als String, damit die Spalten-Auswahl ihn anzeigen kann. */
const KOMPONENTEN_SPALTEN: ColumnDef<Komponente>[] = [
  {
    id: "beschreibung",
    header: "Beschreibung",
    accessorFn: (k) => k.beschreibung,
    cell: ({ row }) => (
      <Link
        to={`/maschinen/komponenten/${row.original.id}`}
        className={`block hover:underline ${FOCUS_LINK_CLASS}`}
      >
        {row.original.beschreibung}
      </Link>
    ),
  },
  {
    id: "tagNummer",
    header: "TAG-Nummer",
    accessorFn: (k) => k.tagNummer ?? "",
    cell: ({ row }) => (
      <span className="font-mono text-xs">{row.original.tagNummer ?? "—"}</span>
    ),
  },
  {
    id: "status",
    header: "Status",
    accessorFn: (k) => (k.gesperrt ? "gesperrt" : "frei"),
    cell: ({ row }) => (
      <Badge tone={row.original.gesperrt ? "red" : "green"}>
        {row.original.gesperrt ? "gesperrt" : "frei"}
      </Badge>
    ),
  },
]

/**
 * Leichte Objekt-Detailseite (P2 aus MASCHINEN_STRUKTURVERGLEICH.md) —
 * Stammdaten + verlinkte Komponenten-Liste + Breadcrumb. Objekte sind laut
 * Befund nur bei 37 von 829 Komponenten die Zwischenebene, daher bewusst
 * schlank gehalten (kein Tab-Layout wie bei der Anlage).
 *
 * Route-Wrapper (Deep-Link-Fallback, `/maschinen/objekte/:id`) — liest die ID
 * aus der URL und delegiert an `ObjektDetailContent`. Primärer Aufrufweg ist
 * das Sheet aus der Objekte-Kindliste der Anlage (UX_KONZEPT.md §7).
 */
export function ObjektDetailPage() {
  const params = useParams<{ id: string }>()
  const objektId = Number(params.id)
  return <ObjektDetailContent objektId={objektId} zeigeBreadcrumb />
}

/** Inhalt der Objekt-Detailansicht — wird von der Route (Deep-Link-Fallback) und
 * vom Sheet (`AnlageDetailPage`-Objekte-Tab) genutzt. Im Sheet wird der
 * Breadcrumb ausgeblendet (Sheet-Kontext zeigt die Anlage bereits dahinter). */
export function ObjektDetailContent({
  objektId,
  zeigeBreadcrumb = false,
}: {
  objektId: number
  zeigeBreadcrumb?: boolean
}) {
  const { data: objekt, isLoading, isError } = useQuery({
    queryKey: ["objekte", objektId],
    queryFn: () => fetchObjekt(objektId),
    enabled: Number.isFinite(objektId),
  })

  const { data: anlage } = useQuery({
    queryKey: ["anlagen", objekt?.anlageId],
    queryFn: () => fetchAnlage(objekt!.anlageId),
    enabled: !!objekt,
  })

  const { data: komponenten } = useQuery({
    queryKey: ["komponenten", "objekt", objektId],
    queryFn: () => fetchKomponenten({ objektId }),
    enabled: Number.isFinite(objektId),
  })

  const { data: pruefungen } = useQuery({
    queryKey: ["pruefungen", "objekt", objektId],
    queryFn: () => fetchPruefungen({ objektId }),
    enabled: Number.isFinite(objektId),
  })

  const { data: wartungen } = useQuery({
    queryKey: ["wartungen", "objekt", objektId],
    queryFn: () => fetchWartungen({ objektId }),
    enabled: Number.isFinite(objektId),
  })

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3" aria-label="Lade Objekt …">
        <div className="h-8 w-1/3 rounded-md bg-slate-100" />
        <div className="h-24 rounded-md bg-slate-100" />
      </div>
    )
  }

  if (isError || !objekt) {
    return <p className="text-sm text-red-700">Objekt konnte nicht geladen werden.</p>
  }

  return (
    <div>
      {zeigeBreadcrumb && (
        <nav className="mb-4 flex flex-wrap items-center gap-1 text-sm text-slate-500" aria-label="Breadcrumb">
          <Link to="/maschinen" className={`hover:underline ${FOCUS_LINK_CLASS}`}>
            Anlagen
          </Link>
          <span aria-hidden="true">›</span>
          {anlage && (
            <Link to={`/maschinen/anlagen/${anlage.id}`} className={`hover:underline ${FOCUS_LINK_CLASS}`}>
              {anlage.bezeichnung}
            </Link>
          )}
          <span aria-hidden="true">›</span>
          <span className="text-slate-700">{objekt.bezeichnung}</span>
        </nav>
      )}

      <header className="mb-6">
        <h1 className="text-2xl font-semibold text-slate-900">{objekt.bezeichnung}</h1>
        <p className="mt-1 text-sm text-slate-600">
          {objekt.objektCode ?? "kein Objekt-Code"} — Zwischenebene zwischen Anlage und Komponente.
        </p>
      </header>

      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-medium text-slate-900">Stammdaten</h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-slate-500">Objekt-Code</dt>
            <dd className="font-mono text-slate-900">{objekt.objektCode ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-slate-500">Beschreibung</dt>
            <dd className="text-slate-900">{objekt.beschreibung ?? "—"}</dd>
          </div>
        </dl>
      </section>

      <section className="mb-6 rounded-lg border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-medium text-slate-900">
          Komponenten ({komponenten?.length ?? 0})
        </h2>
        <DataGrid
          columns={KOMPONENTEN_SPALTEN}
          data={komponenten ?? []}
          storageKey="objekt-detail-komponenten"
          emptyMessage="Keine Komponenten diesem Objekt zugeordnet."
        />
      </section>

      <section className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-3 text-lg font-medium text-slate-900">
            Prüfungen ({pruefungen?.length ?? 0})
          </h2>
          <ul className="space-y-1 text-sm">
            {pruefungen?.map((p) => (
              <li key={p.id}>
                <Link to={`/maschinen/pruefungen/${p.id}`} className={`hover:underline ${FOCUS_LINK_CLASS}`}>
                  Prüfung #{p.id} — {p.pruefungsdatum ?? "kein Datum"}
                </Link>
              </li>
            ))}
            {pruefungen?.length === 0 && <li className="text-slate-400">Keine Prüfungen.</li>}
          </ul>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-6">
          <h2 className="mb-3 text-lg font-medium text-slate-900">
            Wartungen ({wartungen?.length ?? 0})
          </h2>
          <ul className="space-y-1 text-sm">
            {wartungen?.map((w) => (
              <li key={w.id}>
                <Link to={`/maschinen/wartungen/${w.id}`} className={`hover:underline ${FOCUS_LINK_CLASS}`}>
                  Wartung #{w.id} — {w.wartungsdatum ?? "kein Datum"}
                </Link>
              </li>
            ))}
            {wartungen?.length === 0 && <li className="text-slate-400">Keine Wartungen.</li>}
          </ul>
        </div>
      </section>
    </div>
  )
}
